import type LayerViewModel from "../map/LayerViewModel";
import type { ILayerView } from "../model/Layer";
import type BoundsUtil from "./boundsUtil";
import type MapUtil from "./mapUtil";
import type ObjectUtil from "./objectUtil";

const es = global.es; // TODO Remove dependency
export default class MapScopeUtil {
    readonly defaultAddressPointStyle = {"$type": "SymbolStyle", "color": 4278195199, "symbolTypeId": 8, "size": 35};
    readonly defaultTemporaryPointStyle = {"$type": "SymbolStyle", "color": 4294901760, "symbolTypeId": 8, "size": 35};

    private boundsUtil: BoundsUtil
    private mapUtil: MapUtil;
    private objectUtil: ObjectUtil;

    private addAddressPointsMapScopeLayerViewDfds;
    private listenerForRemoveLayer;

    constructor(boundsUtil: BoundsUtil, mapUtil: MapUtil, objectUtil: ObjectUtil) {
        this.boundsUtil = boundsUtil;
        this.mapUtil = mapUtil;
        this.objectUtil = objectUtil;

        this.addAddressPointsMapScopeLayerViewDfds = {}; // should there be only one, as we reload page when changing map, coding for multi-map support nevertheless
        this.listenerForRemoveLayer = null;
    }

    /**
     * 
     * @param map 
     * @param geomType 
     * 
     * @returns 
     */
    private createAddressPointsMapScopeLayerView(map, geomType: string): JQueryDeferred<ILayerView> {
        if (!this.listenerForRemoveLayer){
            this.listenerForRemoveLayer = es.api.getEventChannel().addListener('onRemoveLayerView', this.removeDefferForLayer, this);
        }

        let dfd = this.addAddressPointsMapScopeLayerViewDfds[map.meta.getId()];
        // don't create layer multiple times, wait for previous request to end
        if (dfd) {
            return dfd.promise();
        }

        dfd = this.addAddressPointsMapScopeLayerViewDfds[map.meta.getId()] = $.Deferred();

        es.api.layerView.createDefaultMapScopeCustomPointLayerView(map.meta.getId()).done((newLayerView) => {
            dfd.resolve(newLayerView);
        }).fail(() => {
            delete this.addAddressPointsMapScopeLayerViewDfds[map.meta.getId()];
            dfd.reject.apply(dfd, arguments);
        });

        return dfd.promise();
    }

    /**
     * 
     * @param layerViewId 
     * @param geoMapId 
     * 
     * @returns 
     */
    private removeDefferForLayer(layerViewId: number, geoMapId: number) {
        const dfd = this.addAddressPointsMapScopeLayerViewDfds[geoMapId];
        if (!dfd) {
            return;
        }

        dfd.done((layerView: ILayerView) => {
            if (layerView.id === layerViewId) {
                delete this.addAddressPointsMapScopeLayerViewDfds[geoMapId];
            }
        });
    }

    /**
     * Get the Point Style for the Address/Temporary Points
     *
     * @param isAddressPoint If true the style will be Address Points if false Temporary Points
     * @param map
     */
    getAddressPointStyle(isAddressPoint: boolean, map) {
        let style = null;
        if (isAddressPoint) {
            const layerModel = this.mapUtil.getMapScopeCustomPointLayerViewModel(map.layer);
            // check if Address Points Layer Exists
            if (layerModel) {
                style = layerModel.getStyleSettingsObjRef().defaultStyle;
            } else {
                style = this.defaultAddressPointStyle;
            }
        } else {
            style = this.defaultTemporaryPointStyle;
        }

        return this.objectUtil.deepCopy(style);
    }

    /**
     * Add addressPoints
     *
     * @param addressPoints
     * @param map
     */
    addAddressPoints(addressPoints: any[], map) {
        // check if MapScopeLayerView exists already if not create.
        const geomType = "POINT";
        const mapScopeLayerViewModel = this.mapUtil.getMapScopeCustomPointLayerViewModel(map.layer);
        const dfd = $.Deferred();

        const createAddrPointsOnLvId = (lvId: number) => {
            this.createAddressPoints(lvId, addressPoints,  map.edit).then((rs) => {
                if(addressPoints?.length > 1){
                    try {
                        // TODO: we get the points back in different formats depending on how it was added - should normalise this at root
                        const points = addressPoints[0]?.point ?
                            addressPoints.map(ap => ap.point) : addressPoints.map(ap => [ap.action.geom[1], ap.action.geom[2]]);

                        const bounds = this.boundsUtil.forPoints(points);
                        map.baseMap.setBounds(bounds);
                        //map.overlay.drawBounds(bounds, undefined, 1000);
                    } catch(e){
                        es.log.warn("Could not zoom to AddressPoints:", e);
                    }
                }
                dfd.resolve(rs);
            });
        };

        if (mapScopeLayerViewModel) {
            createAddrPointsOnLvId(mapScopeLayerViewModel.getId());
        } else {
            this.createAddressPointsMapScopeLayerView(map, geomType).then((newLayerView) => {
                setTimeout(() => {
                    createAddrPointsOnLvId(newLayerView.id);
                }, 250);
            });
        }

        return dfd.promise();
    }

    /**
     * Create Address Points
     *
     * @param layerViewId
     * @param addressPoints
     * @param editModule
     */
    createAddressPoints(layerViewId: number, addressPoints: any[], editModule) {
        const dfds = [];
        es.log.log("MapScopeUtil.createAddressPoints", addressPoints);
        for (let i = 0; i < addressPoints.length; i++) {
            
            const geom = addressPoints[i].point ? ["PT", addressPoints[i].point[0], addressPoints[i].point[1]] : null;

            const attrs = {};
            if (addressPoints[i].address){
                attrs.ADDRESS = addressPoints[i].address;
            }
            if(addressPoints[i].hasOwnProperty("name")){
                attrs.NAME = addressPoints[i].name;
            }
            dfds.push(editModule.create(layerViewId, geom, attrs));
        }
        
        return $.when(...dfds).then(() => {
            return editModule.commit();
        });
    }

    /**
     * Add a custom area
     *
     * @param geom to add
     * @param map
     */
    addCustomArea(geom, map): JQueryDeferred<void> {
        // check if MapScopeLayerView exists already if not create.
        const geomType = "POLYGON";
        const layerViewModel = this.mapUtil.getMapScopeCustomAreaLayerViewModel(map.layer);
        if (layerViewModel) {
            return this.createCustomArea(layerViewModel, geom, map);
        }
        
        return es.api.layerView.create({
            "id":-1,
            "$type":"MapScopeCustomAreaLayerView",
            "title":"Custom Areas",
            "geoMapId": map.meta.getId(),
            "geomType": geomType,
            "enabled": true,
            "ordering" : -1,
            "dynamicAttributes": []
        }).then((newLayerView) => {
            return this.createCustomArea(map.layer.getById(newLayerView.id), geom, map);
        });
    }

    /**
     * Create a custom area
     *
     * @param layerViewModel
     * @param geom the area to add
     * @param map
     */
    private createCustomArea(layerViewModel: LayerViewModel, geom, map): JQueryDeferred<void> {
        const attrs = {
            NAME : "Custom Area " + (layerViewModel.getLayerInfo().featureInfo.count + 1)
        };
        
        return map.edit.create(layerViewModel.getId(), geom, attrs).then(() => {
            return map.edit.commit().then((rs: any[]) => {
                for (let i = rs.length - 1; i >= 0; i--) {
                    if (rs[i].$type === "CreateResult") {
                        map.select.select(rs[i].action.layerViewId, rs[i].featureId);
                        break;
                    }
                }
                
                return es.util.deferred.resolved();
            }).fail(() => {
                map.edit.revert();
            });
        });
    }
}
